import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { db, auth } from "../firebase";
import {
    collection,
    getDocs,
    getDoc,
    doc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import Layout from "../components/Layout";
import BlankLayout from "../components/BlankLayout";
import { FaPlus, FaUsers, FaChartBar, FaUpload, FaSignOutAlt, FaHistory, FaChevronRight, FaEdit, FaTrashAlt, FaPowerOff, FaCheckCircle, FaUserShield } from "react-icons/fa";
import "./DsManager.css";

function DsManager() {
    const [userProfile, setUserProfile] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [activeTab, setActiveTab] = useState("create"); // create | list | bulk
    const [distributors, setDistributors] = useState([]);
    const [dsList, setDsList] = useState([]);
    const [loading, setLoading] = useState(true);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ---------- BULK UPLOAD STATE ----------
    const [bulkPreview, setBulkPreview] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    // ---------- CREATE/EDIT DS FORM ----------
    const [newDs, setNewDs] = useState({
        dsId: "",
        name: "",
        phone: "",
        distributorId: "",
        route: "",
        activeOutlets: "",
        vehicleNo: "",
        monArea: "",
        tueArea: "",
        wedArea: "",
        thuArea: "",
        friArea: "",
        satArea: "",
    });

    const metrics = React.useMemo(() => {
        return {
            total: dsList.length,
            active: dsList.filter(d => d.status !== "inactive").length,
            suspended: dsList.filter(d => d.status === "inactive").length,
            hubCoverage: dsList.reduce((acc, d) => acc + Number(d.activeOutlets || 0), 0)
        };
    }, [dsList]);

    // ---------- LOGIN CREATION STATE ----------
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [selectedDsForLogin, setSelectedDsForLogin] = useState(null);
    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [isCreatingLogin, setIsCreatingLogin] = useState(false);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) return;
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
                const profile = snap.data();
                setUserProfile(profile);
                if (profile.role === "rsm") {
                    setActiveTab("list");
                }
                if (profile.role === "distributor") {
                    setNewDs(prev => ({ ...prev, distributorId: profile.partyId || "" }));
                }
                loadDsData(profile);
            }
        });
        return () => unsub();
    }, []);

    const loadDsData = async (profile) => {
        setLoading(true);
        try {
            // Load Distributors for selection
            let distQ = collection(db, "partyMaster");
            if (profile.role !== "superadmin") {
                distQ = query(collection(db, "partyMaster"), where("clientId", "==", profile.clientId));
            }
            const distSnap = await getDocs(distQ);
            let dists = distSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            if (profile.role === "distributor") {
                dists = dists.filter(d => d.partyId === profile.partyId);
            }
            setDistributors(dists);

            // Load DS List
            let dsQ;
            if (profile.role === "distributor") {
                dsQ = query(collection(db, "dsMaster"), where("distributorId", "==", profile.partyId), where("clientId", "==", profile.clientId));
            } else if (profile.role !== "superadmin") {
                dsQ = query(collection(db, "dsMaster"), where("clientId", "==", profile.clientId));
            } else {
                dsQ = query(collection(db, "dsMaster"));
            }
            const dsSnap = await getDocs(dsQ);
            const list = dsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setDsList(list);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const onChangeNewDs = (field, value) => {
        setNewDs((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveDs = async (e) => {
        e.preventDefault();
        if (!newDs.dsId || !newDs.name || !newDs.distributorId) {
            alert("DS ID, Name, and Distributor are required.");
            return;
        }

        const selectedDist = distributors.find((d) => d.partyId === newDs.distributorId);

        const payload = {
            dsId: newDs.dsId.trim(),
            name: newDs.name.trim(),
            phone: newDs.phone.trim(),
            route: newDs.route.trim(),
            activeOutlets: newDs.activeOutlets || 0,
            vehicleNo: newDs.vehicleNo.trim(),
            monArea: newDs.monArea.trim(),
            tueArea: newDs.tueArea.trim(),
            wedArea: newDs.wedArea.trim(),
            thuArea: newDs.thuArea.trim(),
            friArea: newDs.friArea.trim(),
            satArea: newDs.satArea.trim(),
            status: isEditing ? (dsList.find(d => d.dsId === editingId)?.status || "active") : "active",
            distributorId: newDs.distributorId,
            distributorName: selectedDist?.name || "",
            clientId: userProfile.clientId,
        };

        try {
            if (isEditing) {
                await updateDoc(doc(db, "dsMaster", editingId), payload);
                alert("Distributor Salesman updated successfully!");
            } else {
                await setDoc(doc(db, "dsMaster", payload.dsId), payload);
                alert("Distributor Salesman created successfully!");
            }
            setNewDs({
                dsId: "", name: "", phone: "",
                distributorId: userProfile?.role === "distributor" ? userProfile.partyId : "",
                route: "", activeOutlets: "", vehicleNo: "",
                monArea: "", tueArea: "", wedArea: "", thuArea: "", friArea: "", satArea: ""
            });
            setIsEditing(false);
            setEditingId(null);
            loadDsData(userProfile);
            setActiveTab("list");
        } catch (err) {
            console.error(err);
            alert("Error saving DS");
        }
    };

    const handleEditClick = (ds) => {
        setNewDs({
            dsId: ds.dsId,
            name: ds.name,
            phone: ds.phone || "",
            distributorId: ds.distributorId,
            route: ds.route || "",
            activeOutlets: ds.activeOutlets || "",
            vehicleNo: ds.vehicleNo || "",
            monArea: ds.monArea || "",
            tueArea: ds.tueArea || "",
            wedArea: ds.wedArea || "",
            thuArea: ds.thuArea || "",
            friArea: ds.friArea || "",
            satArea: ds.satArea || "",
        });
        setIsEditing(true);
        setEditingId(ds.id);
        setActiveTab("create");
    };

    const cancelEdit = () => {
        setNewDs({
            dsId: "", name: "", phone: "", 
            distributorId: userProfile?.role === "distributor" ? userProfile.partyId : "", 
            route: "", activeOutlets: "", vehicleNo: "",
            monArea: "", tueArea: "", wedArea: "", thuArea: "", friArea: "", satArea: ""
        });
        setIsEditing(false);
        setEditingId(null);
        setActiveTab("list");
    };

    const toggleDsStatus = async (ds) => {
        const newStatus = ds.status === "inactive" ? "active" : "inactive";
        try {
            await updateDoc(doc(db, "dsMaster", ds.id), { status: newStatus });
            loadDsData(userProfile);
        } catch (err) {
            console.error(err);
            alert("Error updating status");
        }
    };

    const handleBulkFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: "binary" });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const data = XLSX.utils.sheet_to_json(ws);

            // Basic validation of data
            const processed = data.map(row => ({
                dsId: String(row.dsId || "").trim(),
                name: String(row.name || "").trim(),
                phone: String(row.phone || "").trim(),
                distributorId: String(row.distributorId || "").trim(),
                route: String(row.route || "").trim(),
                activeOutlets: Number(row.activeOutlets || 0),
                vehicleNo: String(row.vehicleNo || "").trim().toUpperCase(),
                monArea: String(row.monArea || "").trim(),
                tueArea: String(row.tueArea || "").trim(),
                wedArea: String(row.wedArea || "").trim(),
                thuArea: String(row.thuArea || "").trim(),
                friArea: String(row.friArea || "").trim(),
                satArea: String(row.satArea || "").trim(),
            })).filter(r => r.dsId && r.name && r.distributorId);

            setBulkPreview(processed);
        };
        reader.readAsBinaryString(file);
    };

    const confirmBulkUpload = async () => {
        if (bulkPreview.length === 0) return;
        setIsUploading(true);
        try {
            let count = 0;
            for (const ds of bulkPreview) {
                const selectedDist = distributors.find((d) => d.partyId === ds.distributorId);
                const payload = {
                    ...ds,
                    distributorName: selectedDist?.name || "Unknown Distributor",
                    clientId: userProfile.clientId,
                    status: "active",
                };
                await setDoc(doc(db, "dsMaster", ds.dsId), payload);
                count++;
            }
            alert(`Successfully uploaded ${count} Salesmen!`);
            setBulkPreview([]);
            loadDsData(userProfile);
            setActiveTab("list");
        } catch (err) {
            console.error(err);
            alert("Error during bulk upload");
        } finally {
            setIsUploading(false);
        }
    };

    const downloadTemplate = () => {
        const headers = [
            "dsId", "name", "phone", "distributorId", "route", "activeOutlets", "vehicleNo",
            "monArea", "tueArea", "wedArea", "thuArea", "friArea", "satArea"
        ];
        const data = [
            {
                dsId: "DS001",
                name: "John Doe",
                phone: "9876543210",
                distributorId: "DIST001",
                route: "North Route",
                activeOutlets: 45,
                vehicleNo: "KA01AB1234",
                monArea: "Area A",
                tueArea: "Area B",
                wedArea: "Area C",
                thuArea: "Area D",
                friArea: "Area E",
                satArea: "Area F"
            }
        ];

        const ws = XLSX.utils.json_to_sheet(data, { header: headers });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Salesmen");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, "DS_Upload_Template.xlsx");
    };

    const handleDeleteDs = async (ds) => {
        if (!window.confirm(`Delete DS "${ds.name}" ? `)) return;
        try {
            await deleteDoc(doc(db, "dsMaster", ds.id));
            alert("Deleted.");
            loadDsData(userProfile);
        } catch (err) {
            console.error(err);
            alert("Error deleting DS");
        }
    };

    const handleExportData = () => {
        if (dsList.length === 0) {
            alert("No data to export.");
            return;
        }
        const exportData = dsList.map(ds => ({
            "Salesman ID": ds.dsId || "",
            "Name": ds.name || "",
            "Phone": ds.phone || "",
            "Distributor ID": ds.distributorId || "",
            "Distributor Name": ds.distributorName || "",
            "Route/Cluster": ds.route || "",
            "Active Outlets": ds.activeOutlets || 0,
            "Vehicle Number": ds.vehicleNo || "",
            "Mon Area": ds.monArea || "",
            "Tue Area": ds.tueArea || "",
            "Wed Area": ds.wedArea || "",
            "Thu Area": ds.thuArea || "",
            "Fri Area": ds.friArea || "",
            "Sat Area": ds.satArea || "",
            "Status": ds.status || "active"
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Salesmen");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `DS_Master_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleCreateLogin = async (e) => {
        e.preventDefault();
        if (!loginForm.email || !loginForm.password) {
            alert("Email and Password are required.");
            return;
        }

        setIsCreatingLogin(true);
        try {
            const functions = getFunctions();
            const createDsUser = httpsCallable(functions, "createDsUser");
            const result = await createDsUser({
                email: loginForm.email.trim(),
                password: loginForm.password,
                dsId: selectedDsForLogin.dsId,
                name: selectedDsForLogin.name,
                clientId: userProfile.clientId
            });

            if (result.data.success) {
                alert("Login created successfully!");
                setShowLoginModal(false);
                setLoginForm({ email: "", password: "" });
                loadDsData(userProfile);
            }
        } catch (err) {
            console.error("Error calling createDsUser:", err);
            
            // User-Friendly Error Mapping
            let msg = "Could not create login. Please try again.";
            if (err.message?.includes("already-exists") || err.message?.includes("already in use")) {
                msg = "This email is already registered with another account.";
            } else if (err.message?.includes("invalid-email") || err.message?.includes("improperly formatted")) {
                msg = "The email address is improperly formatted.";
            } else if (err.message?.includes("permission-denied") || err.message?.includes("Unauthorized role")) {
                msg = "Authorization Failed: Only Admins or Managers can provision logins.";
            } else if (err.message?.includes("weak-password")) {
                msg = "Password is too weak. Please use at least 8 characters.";
            } else {
                msg = err.message || "An unexpected system error occurred.";
            }
            
            alert(`SECURITY PROTOCOL: ${msg}`);
        } finally {
            setIsCreatingLogin(false);
        }
    };

    const handleLogout = () => auth.signOut();

    if (isMobile) {
        return (
            <BlankLayout>
                <div className="mobile-ds-container">
                    {/* PREMIUM HEADER */}
                    <div className="mobile-ds-header">
                        <div className="header-left">
                            <div className="status-badge">
                                <div className="pulse-dot"></div>
                                <span>SYSTEM ONLINE</span>
                            </div>
                            <h1 className="header-title">MANAGER HUB</h1>
                            <div className="header-sub">{userProfile?.email}</div>
                        </div>
                        <button onClick={handleLogout} className="logout-pill">
                            <FaSignOutAlt />
                        </button>
                    </div>

                    {/* METRICS STRIP */}
                    <div className="mobile-metrics-strip">
                        <div className="metric-mini-card accent-orange">
                            <div className="m-val">{metrics.active}</div>
                            <div className="m-lbl">OPERATIONAL</div>
                        </div>
                        <div className="metric-mini-card accent-blue">
                            <div className="m-val">{metrics.hubCoverage}</div>
                            <div className="m-lbl">HUB COVERAGE</div>
                        </div>
                        <div className="metric-mini-card">
                            <div className="m-val">{metrics.total}</div>
                            <div className="m-lbl">TOTAL FORCE</div>
                        </div>
                    </div>

                    {/* MAIN CONTENT AREA */}
                    <div className="mobile-ds-content">
                        {loading ? (
                            <div className="mobile-loader">⚡ Synchronizing Sales Network...</div>
                        ) : activeTab === "create" && userProfile?.role !== "rsm" ? (
                            <div className="mobile-view-card">
                                <h2 className="mobile-card-title">
                                    {isEditing ? "Modify Executive" : "Provision New Executive"}
                                </h2>
                                <form onSubmit={handleSaveDs} className="mobile-form">
                                    <div className="form-group">
                                        <label>Employee ID / Code</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. DS-001"
                                            value={newDs.dsId}
                                            onChange={(e) => onChangeNewDs("dsId", e.target.value)}
                                            required
                                            disabled={isEditing}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Legal Full Name</label>
                                        <input
                                            type="text"
                                            placeholder="Enter full name"
                                            value={newDs.name}
                                            onChange={(e) => onChangeNewDs("name", e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Contact Number</label>
                                            <input
                                                type="text"
                                                placeholder="+91..."
                                                value={newDs.phone}
                                                onChange={(e) => onChangeNewDs("phone", e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Logistics Cluster / Route</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. North Hub Cluster"
                                            value={newDs.route}
                                            onChange={(e) => onChangeNewDs("route", e.target.value)}
                                        />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>HUB Outlets</label>
                                            <input
                                                type="number"
                                                placeholder="0"
                                                value={newDs.activeOutlets}
                                                onChange={(e) => onChangeNewDs("activeOutlets", e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Vehicle No.</label>
                                            <input
                                                type="text"
                                                placeholder="XX-00-XX-0000"
                                                value={newDs.vehicleNo}
                                                onChange={(e) => onChangeNewDs("vehicleNo", e.target.value.toUpperCase())}
                                            />
                                        </div>
                                    </div>

                                    <div className="beat-matrix-section">
                                        <h4 className="section-tag">WEEKLY BEAT PROTOCOL</h4>
                                        <div className="beat-grid">
                                            {["mon", "tue", "wed", "thu", "fri", "sat"].map(day => (
                                                <div key={day} className="beat-input-wrap">
                                                    <span>{day.toUpperCase()}</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Target"
                                                        value={newDs[`${day}Area`]}
                                                        onChange={e => onChangeNewDs(`${day}Area`, e.target.value)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="form-group" style={{ marginTop: 20 }}>
                                        <label>Parent Entity</label>
                                        <select
                                            value={newDs.distributorId}
                                            onChange={(e) => onChangeNewDs("distributorId", e.target.value)}
                                            required
                                            disabled={userProfile?.role === "distributor"}
                                        >
                                            <option value="">-- Assign Entity --</option>
                                            {distributors.map((d) => (
                                                <option key={d.partyId} value={d.partyId}>
                                                    {d.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-actions-sticky">
                                        <button type="submit" className="mobile-btn-primary">
                                            {isEditing ? "SYNC UPDATES" : "FINALIZE ONBOARDING"}
                                        </button>
                                        {isEditing && (
                                            <button type="button" onClick={cancelEdit} className="mobile-btn-secondary">
                                                ABORT
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        ) : activeTab === "list" ? (
                            <div className="mobile-registry-list">
                                <div className="list-meta" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div>
                                        <span>NETWORK REGISTRY</span>
                                        <span style={{ marginLeft: 8 }}>{dsList.length} EXECUTIVES</span>
                                    </div>
                                    <button 
                                        onClick={handleExportData}
                                        style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "white", padding: "4px 12px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}
                                    >
                                        📥 EXPORT
                                    </button>
                                </div>
                                {dsList.length === 0 ? (
                                    <div className="empty-state">No executives found in network.</div>
                                ) : (
                                    dsList.map(ds => (
                                        <div key={ds.id} className="executive-card">
                                            <div className="card-main">
                                                <div className="exec-info">
                                                    <div className="exec-name">{ds.name}</div>
                                                    <div className="exec-id">ID: {ds.dsId} • {ds.route || "Global"}</div>
                                                </div>
                                                <div className={`exec-status-pill ${ds.status === "inactive" ? "suspended" : "active"}`} onClick={() => toggleDsStatus(ds)}>
                                                    {ds.status === "inactive" ? "SUSPENDED" : "ACTIVE"}
                                                </div>
                                            </div>
                                            <div className="card-details">
                                                <div className="detail-item">
                                                    <span className="d-label">Parent</span>
                                                    <span className="d-val">{ds.distributorName}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="d-label">Contact</span>
                                                    <span className="d-val">{ds.phone || "N/A"}</span>
                                                </div>
                                            </div>
                                            {userProfile?.role !== "rsm" && (
                                                <div className="card-actions">
                                                    <button onClick={() => handleEditClick(ds)} className="action-btn edit">
                                                        <FaEdit /> MANAGE
                                                    </button>
                                                    {!ds.hasLogin && (
                                                        <button onClick={() => { setSelectedDsForLogin(ds); setShowLoginModal(true); }} className="action-btn provision">
                                                            <FaUserShield /> LOGIN
                                                        </button>
                                                    )}
                                                    <button onClick={() => handleDeleteDs(ds)} className="action-btn delete">
                                                        <FaTrashAlt />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        ) : activeTab === "bulk" && userProfile?.role !== "rsm" ? (
                            <div className="mobile-view-card">
                                <h2 className="mobile-card-title">Network Matrix Sync</h2>
                                <p className="mobile-card-sub">Upload manifest protocol for bulk onboarding.</p>
                                
                                <div className="mobile-upload-box">
                                    <div className="upload-icon">📊</div>
                                    <input 
                                        type="file" 
                                        id="ds_up_mob" 
                                        style={{ display: "none" }} 
                                        accept=".xlsx, .xls" 
                                        onChange={handleBulkFileUpload} 
                                    />
                                    <label htmlFor="ds_up_mob" className="mobile-btn-primary" style={{ width: "fit-content", margin: "0 auto" }}>
                                        SELECT MANIFEST
                                    </label>
                                    <button onClick={downloadTemplate} className="mobile-link-btn">
                                        Download Template
                                    </button>
                                </div>

                                {bulkPreview.length > 0 && (
                                    <div className="mobile-preview-area">
                                        <div className="preview-header">PREVIEW: {bulkPreview.length} RECORDS</div>
                                        <div className="preview-list">
                                            {bulkPreview.slice(0, 5).map((p, i) => (
                                                <div key={i} className="preview-item">
                                                    <strong>{p.name}</strong> ({p.dsId})
                                                </div>
                                            ))}
                                            {bulkPreview.length > 5 && <div className="preview-more">+{bulkPreview.length - 5} more...</div>}
                                        </div>
                                        <button onClick={confirmBulkUpload} className="mobile-btn-primary" disabled={isUploading}>
                                            {isUploading ? "EXECUTING..." : `SYNC ${bulkPreview.length} EXECUTIVES`}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>

                    {/* BOTTOM NAV */}
                    <div className="mobile-ds-nav">
                        {[
                            { id: "create", label: "ONBOARD", icon: <FaPlus />, hideFor: ["rsm"] },
                            { id: "list", label: "REGISTRY", icon: <FaUsers /> },
                            { id: "bulk", label: "SYNC", icon: <FaUpload />, hideFor: ["rsm"] }
                        ].filter(tab => !tab.hideFor || !tab.hideFor.includes(userProfile?.role)).map(tab => (
                            <div key={tab.id} className={`nav-item ${activeTab === tab.id ? "active" : ""}`} onClick={() => { cancelEdit(); setActiveTab(tab.id); }}>
                                {tab.icon}
                                <span>{tab.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* MODALS */}
                    {showLoginModal && (
                        <div className="mobile-modal-overlay">
                            <div className="mobile-modal premium-modal">
                                <div className="modal-top-bar"></div>
                                <div className="modal-header-pro">
                                    <div className="icon-wrap-pro"><FaUserShield /></div>
                                    <div className="title-wrap-pro">
                                        <h3>Secure Provisioning</h3>
                                        <p>Activate mobile dashboard access</p>
                                    </div>
                                </div>

                                <div className="target-person-badge">
                                    <div className="p-avatar">{selectedDsForLogin?.name?.charAt(0)}</div>
                                    <div className="p-info">
                                        <div className="p-name">{selectedDsForLogin?.name}</div>
                                        <div className="p-id">EXECUTIVE ID: {selectedDsForLogin?.dsId}</div>
                                    </div>
                                </div>

                                <form onSubmit={handleCreateLogin} className="mobile-form-pro">
                                    <div className="form-group-pro">
                                        <label>NETWORK IDENTITY (EMAIL)</label>
                                        <input
                                            type="email"
                                            required
                                            value={loginForm.email}
                                            onChange={e => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                                            placeholder="executive@quickbill.com"
                                        />
                                    </div>
                                    <div className="form-group-pro">
                                        <label>ACCESS PROTOCOL (PASSWORD)</label>
                                        <input
                                            type="text"
                                            required
                                            value={loginForm.password}
                                            onChange={e => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                                            placeholder="Min 8 characters"
                                        />
                                    </div>
                                    <div className="modal-actions-pro">
                                        <button type="submit" disabled={isCreatingLogin} className="mobile-btn-primary pulse-btn">
                                            {isCreatingLogin ? "SYNCING..." : "AUTHORIZE ACCESS"}
                                        </button>
                                        <button type="button" onClick={() => setShowLoginModal(false)} className="mobile-btn-cancel">
                                            DISMISS
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </BlankLayout>
        );
    }

    return (
        <Layout>
            <div className="layout-content">
                <div className="ds-header" style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div>
                        <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Sales Force Command</h1>
                        <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Onboard salesmen, coordinate route matrices, and manage field operational status.</p>
                    </div>
                    <div className="ds-metrics" style={{ display: "flex", gap: 30 }}>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 24, fontWeight: 800, color: "var(--primary)" }}>{metrics.active}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Operational</div>
                        </div>
                        <div className="ds-metrics-divider" style={{ width: 1, height: 40, background: "var(--border)" }}></div>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 24, fontWeight: 800, color: "var(--text-main)" }}>{metrics.hubCoverage}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Hub Coverage</div>
                        </div>
                    </div>
                </div>

                <div className="ds-tabs" style={{ 
                    display: "flex", 
                    gap: 8, 
                    marginBottom: 32, 
                    padding: 6, 
                    background: "var(--surface-hover)", 
                    borderRadius: 14, 
                    width: "fit-content",
                    border: "1px solid var(--border)"
                }}>
                    {[
                        { id: "create", label: isEditing ? "Editing Executive" : "Manual Onboarding", hideFor: ["rsm"] },
                        { id: "list", label: "Network Registry" },
                        { id: "bulk", label: "Matrix Synchronize", hideFor: ["rsm"] }
                    ].filter(tab => !tab.hideFor || !tab.hideFor.includes(userProfile?.role)).map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => { cancelEdit(); setActiveTab(tab.id); }}
                            style={{
                                padding: "10px 20px",
                                borderRadius: 10,
                                border: "none",
                                background: activeTab === tab.id ? "var(--surface)" : "transparent",
                                color: activeTab === tab.id ? "var(--primary)" : "var(--text-muted)",
                                fontWeight: 700,
                                fontSize: 13,
                                cursor: "pointer",
                                transition: "all 0.2s",
                                boxShadow: activeTab === tab.id ? "var(--shadow-sm)" : "none"
                            }}
                        >
                            {tab.id === "create" && (isEditing ? "✏️ " : "➕ ")}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: "center", color: "var(--text-muted)" }}>Synchronizing sales network...</div>
                ) : activeTab === "create" && userProfile?.role !== "rsm" ? (
                    <div className="premium-card" style={{ maxWidth: 900, padding: 32 }}>
                        <h2 style={{ fontSize: 22, marginBottom: 24 }}>{isEditing ? `System Record: ${editingId}` : "Provision New Sales Executive"}</h2>
                        <form onSubmit={handleSaveDs}>
                            <div className="ds-form-grid-2-1" style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 24, marginBottom: 24 }}>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Employee ID / Code</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="e.g. DS-001"
                                        value={newDs.dsId}
                                        onChange={(e) => onChangeNewDs("dsId", e.target.value)}
                                        required
                                        disabled={isEditing}
                                        style={{ width: "100%", background: isEditing ? "var(--surface-hover)" : "var(--surface)" }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Legal Full Name</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Enter full name for payroll/registry"
                                        value={newDs.name}
                                        onChange={(e) => onChangeNewDs("name", e.target.value)}
                                        required
                                        style={{ width: "100%" }}
                                    />
                                </div>
                            </div>

                            <div className="ds-form-grid-1-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Contact Number</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="+91..."
                                        value={newDs.phone}
                                        onChange={(e) => onChangeNewDs("phone", e.target.value)}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Logistics Cluster</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="e.g. North Hub Cluster"
                                        value={newDs.route}
                                        onChange={(e) => onChangeNewDs("route", e.target.value)}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                            </div>

                            <div className="ds-form-grid-1-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Operational Outlets</label>
                                    <input
                                        type="number"
                                        className="form-input"
                                        placeholder="0"
                                        value={newDs.activeOutlets}
                                        onChange={(e) => onChangeNewDs("activeOutlets", e.target.value)}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Vehicle Registration</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="XX-00-XX-0000"
                                        value={newDs.vehicleNo}
                                        onChange={(e) => onChangeNewDs("vehicleNo", e.target.value.toUpperCase())}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                            </div>

                            <div style={{ padding: 24, borderRadius: 16, background: "var(--surface-hover)", border: "1px solid var(--border)", marginBottom: 24 }}>
                                <h4 style={{ margin: "0 0 20px 0", color: "var(--primary)", fontSize: 14, fontWeight: 800, letterSpacing: "0.05em" }}>WEEKLY ROUTE MATRIX</h4>
                                <div className="ds-route-matrix" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                                    {["mon", "tue", "wed", "thu", "fri", "sat"].map(day => (
                                        <div key={day}>
                                            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>{day}</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                placeholder="Target Area"
                                                value={newDs[`${day}Area`]}
                                                onChange={e => onChangeNewDs(`${day}Area`, e.target.value)}
                                                style={{ width: "100%", background: "white" }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Parent Entity (Distributor)</label>
                                <select
                                    className="form-input"
                                    value={newDs.distributorId}
                                    onChange={(e) => onChangeNewDs("distributorId", e.target.value)}
                                    required
                                    style={{ width: "100%" }}
                                    disabled={userProfile?.role === "distributor"}
                                >
                                    <option value="">-- Assign Entity --</option>
                                    {distributors.map((d) => (
                                        <option key={d.partyId} value={d.partyId}>
                                            {d.name} ({d.partyId})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ display: "flex", gap: 16 }}>
                                <button type="submit" className="btn-primary" style={{ flex: 1, padding: 16, fontSize: 16 }}>
                                    {isEditing ? "Sync Profile Updates" : "Finalize Network Onboarding"}
                                </button>
                                {isEditing && (
                                    <button type="button" onClick={cancelEdit} className="btn-secondary" style={{ padding: "0 32px" }}>
                                        Abort
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                ) : activeTab === "bulk" && userProfile?.role !== "rsm" ? (
                    <div className="premium-card" style={{ maxWidth: 840, padding: 32 }}>
                        <h2 style={{ fontSize: 22, marginBottom: 12 }}>Network Matrix Sync</h2>
                        <p style={{ color: "var(--text-muted)", fontSize: 14, marginBottom: 32 }}>Onboard entire regional sales teams via standardized manifest protocols.</p>

                        <div className="ds-dropzone" style={{ padding: 40, borderRadius: 20, background: "var(--surface-hover)", border: "2px dashed var(--border)", textAlign: "center", marginBottom: 32 }}>
                            <div style={{ fontSize: 56, marginBottom: 20 }}>📊</div>
                            <h4 style={{ marginBottom: 8, fontSize: 18 }}>Manifest Dropzone</h4>
                            <p style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 32, maxWidth: 400, margin: "0 auto 32px" }}>Select a network manifest following the executive protocol.</p>
                            
                            <div className="ds-dropzone-actions" style={{ display: "flex", justifyContent: "center", gap: 16 }}>
                                <input 
                                    type="file" 
                                    id="ds_up" 
                                    style={{ display: "none" }} 
                                    accept=".xlsx, .xls" 
                                    onChange={handleBulkFileUpload} 
                                />
                                <label htmlFor="ds_up" className="btn-primary" style={{ cursor: "pointer", padding: "14px 28px", display: "inline-block" }}>
                                    Execute File Pulse
                                </label>
                                <button
                                    onClick={downloadTemplate}
                                    className="btn-secondary"
                                    style={{ padding: "0 28px" }}
                                >
                                    📥 Download Executive Template
                                </button>
                            </div>
                        </div>

                        {bulkPreview.length > 0 && (
                            <div style={{ animation: "fadeIn 0.3s ease-out" }}>
                                <div style={{ padding: "12px 16px", background: "var(--surface-hover)", borderTopLeftRadius: 12, borderTopRightRadius: 12, border: "1px solid var(--border)", borderBottom: "none", fontSize: 12, fontWeight: 700 }}>
                                    PREVIEW: {bulkPreview.length} RECORDS AUTHENTICATED
                                </div>
                                <div style={{ maxHeight: 400, overflowY: "auto", border: "1px solid var(--border)", borderRadius: "0 0 12px 12px", background: "white" }}>
                                    <table className="enterprise-table">
                                        <thead>
                                            <tr>
                                                <th>Executive ID</th>
                                                <th>Full Name</th>
                                                <th>Parent Entity</th>
                                                <th>Initial Route</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bulkPreview.map((p, i) => (
                                                <tr key={i}>
                                                    <td style={{ fontWeight: 700 }}>{p.dsId}</td>
                                                    <td>{p.name}</td>
                                                    <td>{p.distributorId}</td>
                                                    <td><span className="tb-status-pill" style={{ background: "var(--surface-hover)", color: "var(--text-main)" }}>{p.route}</span></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <button
                                    onClick={confirmBulkUpload}
                                    disabled={isUploading}
                                    className="btn-primary"
                                    style={{ width: "100%", marginTop: 24, padding: 16, fontSize: 16 }}
                                >
                                    {isUploading ? "Executing Network Infusion..." : `Onboard ${bulkPreview.length} Executives`}
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="premium-card" style={{ padding: 0 }}>
                        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0 }}>Active Network Force</h3>
                            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                <button 
                                    onClick={handleExportData}
                                    className="btn-secondary"
                                    style={{ padding: "8px 16px", fontSize: 12, borderRadius: 10, display: "flex", alignItems: "center", gap: 6 }}
                                >
                                    📥 Export Registry
                                </button>
                                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{dsList.length} Connected Executives</div>
                            </div>
                        </div>
                        <div style={{ maxHeight: 700, overflowY: "auto" }}>
                            <table className="enterprise-table">
                                <thead>
                                    <tr>
                                        <th>Identity & Reach</th>
                                        <th>Logistic Matrix</th>
                                        <th>Parent Entity</th>
                                        <th>Weekly Protocol</th>
                                        <th>Account Status</th>
                                        {userProfile?.role !== "rsm" && <th style={{ textAlign: "right" }}>Management</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {dsList.length === 0 ? (
                                        <tr><td colSpan="6" style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Sales force empty. Initialize onboarding to begin distribution.</td></tr>
                                    ) : (
                                        dsList.map((ds) => (
                                            <tr key={ds.id}>
                                                <td data-label="Identity & Reach">
                                                    <div style={{ fontWeight: 800, color: "var(--primary)", fontSize: 15 }}>{ds.name}</div>
                                                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{ds.phone || "No contact verified"}</div>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginTop: 4 }}>EX-ID: {ds.dsId}</div>
                                                </td>
                                                <td data-label="Logistic Matrix">
                                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{ds.route || "No Cluster"}</div>
                                                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{ds.vehicleNo || "---"}</div>
                                                    <div style={{ fontSize: 10, color: "var(--primary)", fontWeight: 700, marginTop: 4 }}>{ds.activeOutlets || 0} HUB RECENT</div>
                                                </td>
                                                <td data-label="Parent Entity">
                                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{ds.distributorName}</div>
                                                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>ID: {ds.distributorId}</div>
                                                </td>
                                                <td data-label="Weekly Protocol">
                                                    <div style={{ fontSize: 9, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 8px", width: 140 }}>
                                                        <div><span style={{ color: "var(--primary)", fontWeight: 800 }}>M:</span> {ds.monArea || "-"}</div>
                                                        <div><span style={{ color: "var(--primary)", fontWeight: 800 }}>T:</span> {ds.tueArea || "-"}</div>
                                                        <div><span style={{ color: "var(--primary)", fontWeight: 800 }}>W:</span> {ds.wedArea || "-"}</div>
                                                        <div><span style={{ color: "var(--primary)", fontWeight: 800 }}>T:</span> {ds.thuArea || "-"}</div>
                                                        <div><span style={{ color: "var(--primary)", fontWeight: 800 }}>F:</span> {ds.friArea || "-"}</div>
                                                        <div><span style={{ color: "var(--primary)", fontWeight: 800 }}>S:</span> {ds.satArea || "-"}</div>
                                                    </div>
                                                </td>
                                                <td data-label="Account Status">
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                        <div
                                                            onClick={() => toggleDsStatus(ds)}
                                                            className={`tb-status-pill ${ds.status === "inactive" ? "status-offline" : "status-online"}`}
                                                            style={{ cursor: "pointer", display: "inline-flex" }}
                                                        >
                                                            <div className="status-dot"></div>
                                                            {ds.status === "inactive" ? "Suspended" : "Operational"}
                                                        </div>
                                                        {ds.hasLogin ? (
                                                            <div style={{ fontSize: 10, color: "var(--success)", fontWeight: 800, paddingLeft: 10 }}>● PORTAL ACTIVE</div>
                                                        ) : (
                                                            userProfile?.role !== "rsm" ? (
                                                              <button 
                                                                  onClick={() => { setSelectedDsForLogin(ds); setShowLoginModal(true); }}
                                                                  style={{ fontSize: 10, background: "transparent", border: "none", color: "var(--primary)", fontWeight: 800, cursor: "pointer", paddingLeft: 10, textAlign: "left" }}
                                                              >
                                                                  PROVISION LOGIN
                                                              </button>
                                                            ) : (
                                                              <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 800, paddingLeft: 10 }}>ACCESS PENDING</div>
                                                            )
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: "right" }}>
                                                    {userProfile?.role !== "rsm" && (
                                                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                                                            <button onClick={() => handleEditClick(ds)} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid var(--primary)", background: "transparent", color: "var(--primary)", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>Profile</button>
                                                            <button onClick={() => handleDeleteDs(ds)} style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: "rgba(239, 68, 68, 0.1)", color: "var(--error)", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>Retire</button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {showLoginModal && (
                    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.8)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
                        <div className="premium-card" style={{ maxWidth: 460, width: "100%", padding: 32, animation: "modalSlideUp 0.3s ease-out" }}>
                            <h3 style={{ fontSize: 20, marginBottom: 8 }}>Secure Portal Provisioning</h3>
                            <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 24 }}>Initializing mobile dashboard access for <strong>{selectedDsForLogin?.name}</strong>.</p>
                            
                            <form onSubmit={handleCreateLogin}>
                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "var(--text-main)", marginBottom: 6 }}>Login Identity (Email)</label>
                                    <input
                                        type="email"
                                        className="form-input"
                                        required
                                        style={{ width: "100%" }}
                                        value={loginForm.email}
                                        onChange={e => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                                        placeholder="executive.name@quickbill.com"
                                    />
                                </div>
                                <div style={{ marginBottom: 24 }}>
                                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "var(--text-main)", marginBottom: 6 }}>Secure Access Protocol (Password)</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        required
                                        style={{ width: "100%" }}
                                        value={loginForm.password}
                                        onChange={e => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                                        placeholder="Min 8 characters/Alpha-numeric"
                                    />
                                </div>
                                <div style={{ display: "flex", gap: 12 }}>
                                    <button
                                        type="submit"
                                        disabled={isCreatingLogin}
                                        className="btn-primary"
                                        style={{ flex: 1, padding: 14 }}
                                    >
                                        {isCreatingLogin ? "Provisioning..." : "Finalize Credential Pulse"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowLoginModal(false)}
                                        className="btn-secondary"
                                        style={{ padding: "0 20px" }}
                                    >
                                        Abort
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
export default DsManager;
